import { Badge } from '@/components/ui-kit/badge';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Switch } from '@/components/ui-kit/switch';
import type { ConditionGroup } from '@/lib/templates/conditions';
import { cn } from '@/lib/utils';
import type { TemplateField } from '@/modules/templates/types/templates.types';
import { TemplateFieldType as FieldType } from '@/modules/templates/types/templates.types';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlignLeft,
  Building2,
  Calendar,
  CheckSquare,
  ChevronDown,
  ChevronUp,
  DollarSign,
  FileText,
  GitBranch,
  Hash,
  Link2,
  List,
  Mail,
  MapPin,
  Percent,
  Phone,
  Plus,
  Sparkles,
  Type,
  Users,
  Wand2,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { AIEnrichmentDialog } from './ai-enrichment-dialog';
import { AISettingsDialog, type AvailableContextKey } from './ai-settings-dialog';
import { ConditionEditorDialog, type AvailableField } from './condition-editor-dialog';
import { useBuilder, type ScreenWithFields } from './typeform-builder';

// Helper function to extract context keys from aiOutputSchema
function extractContextKeysFromSchema(
  schema: string | null | undefined,
  screenTitle: string
): AvailableContextKey[] {
  if (!schema) return [];

  try {
    const parsed = JSON.parse(schema);
    if (parsed.type !== 'object' || !parsed.properties) return [];

    const keys: AvailableContextKey[] = [];

    const flattenProperties = (properties: Record<string, any>, prefix = ''): void => {
      Object.entries(properties).forEach(([key, value]: [string, any]) => {
        const fullPath = prefix ? `${prefix}.${key}` : key;
        keys.push({
          screenTitle,
          key,
          type: value.type || 'unknown',
          fullPath,
        });

        // Recursively handle nested objects
        if (value.type === 'object' && value.properties) {
          flattenProperties(value.properties, fullPath);
        }
      });
    };

    flattenProperties(parsed.properties);
    return keys;
  } catch {
    return [];
  }
}

// Field type options
const fieldTypeOptions: {
  value: FieldType;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  { value: 'text', icon: Type },
  { value: 'textarea', icon: AlignLeft },
  { value: 'email', icon: Mail },
  { value: 'number', icon: Hash },
  { value: 'date', icon: Calendar },
  { value: 'phone', icon: Phone },
  { value: 'select', icon: List },
  { value: 'multiselect', icon: CheckSquare },
  { value: 'checkbox', icon: CheckSquare },
  { value: 'address', icon: MapPin },
  { value: 'party', icon: Building2 },
  { value: 'currency', icon: DollarSign },
  { value: 'percentage', icon: Percent },
  { value: 'url', icon: Link2 },
];

// Screen type options
const screenTypeOptions = [
  { value: 'standard', icon: FileText },
  { value: 'dynamic', icon: Sparkles },
  { value: 'signatory', icon: Users },
];

interface PropertiesPanelProps {
  selectedScreen: ScreenWithFields | null;
  selectedField: TemplateField | null;
  allScreens: ScreenWithFields[];
  /** When true, the panel fills its parent (e.g. a mobile bottom-sheet)
      instead of using its fixed desktop width. */
  isMobile?: boolean;
}

export function PropertiesPanel({
  selectedScreen,
  selectedField,
  allScreens,
  isMobile = false,
}: PropertiesPanelProps) {
  // const { updateField, updateScreen, selection } = useBuilder();
  const { t } = useTranslation();

  if (!selectedScreen) {
    return (
      <aside
        className={cn(
          'bg-[hsl(var(--bg))] p-4 shrink-0',
          isMobile ? 'w-full h-full' : 'w-72 border-l border-border'
        )}
      >
        <p className="text-sm text-globe-grey text-center mt-8">
          {t('TEMPLATEBUILDER_PROPERTIESPANEL_EMPTYSTATE')}
        </p>
      </aside>
    );
  }

  return (
    <aside
      className={cn(
        'bg-[hsl(var(--bg))] flex flex-col shrink-0 overflow-hidden',
        isMobile ? 'w-full h-full' : 'w-72 border-l border-border'
      )}
    >
      <AnimatePresence mode="wait">
        {selectedField ? (
          <FieldProperties
            key={selectedField.id}
            field={selectedField}
            screenId={selectedScreen.id}
            allScreens={allScreens}
          />
        ) : (
          <ScreenProperties key={selectedScreen.id} screen={selectedScreen} />
        )}
      </AnimatePresence>
    </aside>
  );
}

// Field properties component
function FieldProperties({
  field,
  screenId,
  allScreens,
}: {
  field: TemplateField;
  screenId: string;
  allScreens: ScreenWithFields[];
}) {
  const { updateField } = useBuilder();
  const { t } = useTranslation();
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [newOption, setNewOption] = useState('');
  const [conditionDialogOpen, setConditionDialogOpen] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiEnabled, setAiEnabled] = useState((field as any).aiSuggestionEnabled || false);
  const [aiKey, setAiKey] = useState((field as any).aiSuggestionKey || '');
  const [conditions, setConditions] = useState<ConditionGroup | null>(() => {
    const fieldConditions = (field as any).conditions;
    if (fieldConditions) {
      try {
        return typeof fieldConditions === 'string' ? JSON.parse(fieldConditions) : fieldConditions;
      } catch {
        return null;
      }
    }
    return null;
  });

  // Local state for text inputs (for immediate UI updates)
  const [localPlaceholder, setLocalPlaceholder] = useState(field.placeholder || '');
  const [localName, setLocalName] = useState(field.name);

  // Debounced update handlers (500ms delay)
  const debouncedUpdatePlaceholder = useDebouncedCallback((value: string) => {
    updateField(field.id, { placeholder: value || undefined });
  }, 1500);

  const debouncedUpdateName = useDebouncedCallback((value: string) => {
    updateField(field.id, { name: value });
  }, 1500);

  // Update local state when field changes
  useEffect(() => {
    setAiEnabled((field as any).aiSuggestionEnabled || false);
    setAiKey((field as any).aiSuggestionKey || '');
    setLocalPlaceholder(field.placeholder || '');
    setLocalName(field.name);
    const fieldConditions = (field as any).conditions;
    if (fieldConditions) {
      try {
        setConditions(
          typeof fieldConditions === 'string' ? JSON.parse(fieldConditions) : fieldConditions
        );
      } catch {
        setConditions(null);
      }
    } else {
      setConditions(null);
    }
  }, [field]);

  // Cancel pending updates when field changes
  useEffect(() => {
    return () => {
      debouncedUpdatePlaceholder.cancel();
      debouncedUpdateName.cancel();
    };
  }, [field.id, debouncedUpdatePlaceholder, debouncedUpdateName]);

  // Compute available fields for conditions (fields from previous screens + earlier fields in current screen)
  const availableFieldsForConditions = useMemo<AvailableField[]>(() => {
    const result: AvailableField[] = [];
    const currentScreenIndex = allScreens.findIndex((s) => s.id === screenId);
    const currentScreen = allScreens[currentScreenIndex];
    const currentFieldIndex = currentScreen?.fields.findIndex((f) => f.id === field.id);

    // Fields from all previous screens
    for (let i = 0; i < currentScreenIndex; i++) {
      const screen = allScreens[i];
      for (const f of screen.fields) {
        result.push({
          name: f.name,
          label: f.label,
          screenTitle: screen.title,
          type: f.type,
          options: f.options,
        });
      }
    }

    // Fields from current screen that come before this field
    if (currentScreen && currentFieldIndex !== undefined && currentFieldIndex > 0) {
      for (let i = 0; i < currentFieldIndex; i++) {
        const f = currentScreen.fields[i];
        result.push({
          name: f.name,
          label: f.label,
          screenTitle: currentScreen.title,
          type: f.type,
          options: f.options,
        });
      }
    }

    return result;
  }, [allScreens, screenId, field.id]);

  // Calculate field index for display
  const fieldIndex = useMemo(() => {
    const currentScreenIndex = allScreens.findIndex((s) => s.id === screenId);
    let totalFields = 0;
    for (let i = 0; i < currentScreenIndex; i++) {
      totalFields += allScreens[i].fields.length;
    }
    const currentScreen = allScreens[currentScreenIndex];
    const currentFieldIndex = currentScreen?.fields.findIndex((f) => f.id === field.id);
    return totalFields + (currentFieldIndex ?? 0) + 1;
  }, [allScreens, screenId, field.id]);

  // Compute available AI context keys from previous screens' aiOutputSchema
  const availableContextKeys = useMemo<AvailableContextKey[]>(() => {
    const keys: AvailableContextKey[] = [];
    const currentScreenIndex = allScreens.findIndex((s) => s.id === screenId);

    // Get keys from all previous screens that have aiOutputSchema
    for (let i = 0; i < currentScreenIndex; i++) {
      const screen = allScreens[i];
      const schemaKeys = extractContextKeysFromSchema((screen as any).aiOutputSchema, screen.title);
      keys.push(...schemaKeys);
    }

    return keys;
  }, [allScreens, screenId]);

  const selectedTypeOption = fieldTypeOptions.find((t) => t.value === field.type);
  const TypeIcon = selectedTypeOption?.icon || Type;

  const handleTypeChange = (newType: FieldType) => {
    updateField(field.id, { type: newType });
    setShowTypeSelector(false);
  };

  const addOption = () => {
    if (newOption.trim() && !field.options?.includes(newOption.trim())) {
      const updatedOptions = [...(field.options || []), newOption.trim()];
      updateField(field.id, { options: updatedOptions });
      setNewOption('');
    }
  };

  const removeOption = (index: number) => {
    const updatedOptions = (field.options || []).filter((_, i) => i !== index);
    updateField(field.id, { options: updatedOptions });
  };

  const handleConditionsChange = (newConditions: ConditionGroup | null) => {
    setConditions(newConditions);
    updateField(field.id, {
      conditions: newConditions ? JSON.stringify(newConditions) : null,
    } as any);
  };

  const handleAIChange = (enabled: boolean, key: string) => {
    setAiEnabled(enabled);
    setAiKey(key);
    updateField(field.id, {
      aiSuggestionEnabled: enabled,
      aiSuggestionKey: enabled ? key : null,
    } as any);
  };

  const hasLogicConfigured = conditions || aiEnabled;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex-1 overflow-y-auto"
    >
      {/* Header */}
      <div className="p-4 border-b border-border">
        <h3 className="font-semibold text-[hsl(var(--fg))]">
          {t('TEMPLATEBUILDER_PROPERTIESPANEL_QUESTIONHEADER')}
        </h3>
      </div>

      <div className="p-4 space-y-6">
        {/* Answer Type Selector */}
        <div className="space-y-2">
          <Label className="text-sm text-globe-grey">
            {t('TEMPLATEBUILDER_PROPERTIESPANEL_ANSWERLABEL')}
          </Label>
          <button
            onClick={() => setShowTypeSelector(!showTypeSelector)}
            className="flex items-center justify-between w-full p-3 rounded-lg border border-border hover:border-selise-blue/50 transition-colors bg-[hsl(var(--bg))]"
          >
            <div className="flex items-center gap-2">
              <TypeIcon className="h-4 w-4 text-globe-grey" />
              <span className="text-sm font-medium text-[hsl(var(--fg))]">
                {t(`TEMPLATEBUILDER_PROPERTIESPANEL_FIELDTYPES_${field.type?.toUpperCase()}`)}
              </span>
            </div>
            <ChevronDown
              className={cn(
                'h-4 w-4 text-globe-grey transition-transform',
                showTypeSelector && 'rotate-180'
              )}
            />
          </button>

          <AnimatePresence>
            {showTypeSelector && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="grid grid-cols-2 gap-1 p-2 mt-2 rounded-lg border border-border bg-muted/30">
                  {fieldTypeOptions.map((option) => {
                    const Icon = option.icon;
                    const isSelected = field.type === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => handleTypeChange(option.value)}
                        className={cn(
                          'flex items-center gap-2 p-2 rounded-lg text-left text-sm transition-colors',
                          isSelected
                            ? 'bg-selise-blue text-white'
                            : 'text-[hsl(var(--fg))] hover:bg-muted'
                        )}
                      >
                        <Icon
                          className={cn('h-4 w-4', isSelected ? 'text-white' : 'text-globe-grey')}
                        />
                        <span className="truncate">
                          {t(
                            `TEMPLATEBUILDER_PROPERTIESPANEL_FIELDTYPES_${option.value?.toUpperCase()}`
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Required Toggle */}
        <div className="flex items-center justify-between">
          <Label className="text-sm text-[hsl(var(--fg))]">
            {t('TEMPLATEBUILDER_PROPERTIESPANEL_REQUIRED')}
          </Label>
          <Switch
            checked={field.required}
            onCheckedChange={(checked) => updateField(field.id, { required: checked })}
          />
        </div>

        {/* Options for select/multiselect */}
        {(field.type === 'select' || field.type === 'multiselect') && (
          <div className="space-y-3">
            <Label className="text-sm text-[hsl(var(--fg))]">
              {t('TEMPLATEBUILDER_PROPERTIESPANEL_OPTIONS')}
            </Label>
            <div className="space-y-2">
              {(field.options || []).map((option, index) => (
                <div key={index} className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
                  <span className="flex-1 text-sm truncate text-[hsl(var(--fg))]">{option}</span>
                  <button
                    onClick={() => removeOption(index)}
                    className="p-1 hover:bg-destructive/10 rounded"
                  >
                    <X className="h-3 w-3 text-destructive" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                placeholder={t('TEMPLATEBUILDER_PROPERTIESPANEL_ADDOPTIONPLACEHOLDER')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addOption();
                  }
                }}
                className="flex-1"
              />
              <button
                onClick={addOption}
                disabled={!newOption.trim()}
                className="p-2 rounded-lg bg-selise-blue text-white disabled:opacity-50"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Placeholder */}
        {field.type !== 'checkbox' && (
          <div className="space-y-2">
            <Label className="text-sm text-[hsl(var(--fg))]">
              {t('TEMPLATEBUILDER_PROPERTIESPANEL_PLACEHOLDER')}
            </Label>
            <Input
              value={localPlaceholder}
              onChange={(e) => {
                const value = e.target.value;
                setLocalPlaceholder(value);
                debouncedUpdatePlaceholder(value);
              }}
              onBlur={() => {
                debouncedUpdatePlaceholder.flush();
              }}
              placeholder={t('TEMPLATEBUILDER_PROPERTIESPANEL_ANSWERPLACEHOLDER')}
            />
          </div>
        )}

        {/* Advanced section */}
        <div className="border-t border-border pt-4">
          <button
            onClick={() => setAdvancedOpen(!advancedOpen)}
            className="flex items-center justify-between w-full text-left"
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-[hsl(var(--fg))]">
                {t('TEMPLATEBUILDER_PROPERTIESPANEL_ADVANCED')}
              </span>
              {hasLogicConfigured && <span className="h-2 w-2 rounded-full bg-selise-blue" />}
            </div>
            {advancedOpen ? (
              <ChevronUp className="h-4 w-4 text-globe-grey" />
            ) : (
              <ChevronDown className="h-4 w-4 text-globe-grey" />
            )}
          </button>

          <AnimatePresence>
            {advancedOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="pt-4 space-y-4">
                  {/* Field Name */}
                  <div className="space-y-2">
                    <Label className="text-sm text-[hsl(var(--fg))]">
                      {t('TEMPLATEBUILDER_PROPERTIESPANEL_FIELDNAME')}
                    </Label>
                    <Input
                      value={localName}
                      onChange={(e) => {
                        const value = e.target.value;
                        setLocalName(value);
                        debouncedUpdateName(value);
                      }}
                      onBlur={() => {
                        debouncedUpdateName.flush();
                      }}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-globe-grey">
                      {t('TEMPLATEBUILDER_PROPERTIESPANEL_FIELDNAMEHINT')}
                    </p>
                  </div>

                  {/* Conditional Visibility Button */}
                  <button
                    type="button"
                    onClick={() => setConditionDialogOpen(true)}
                    className="w-full flex items-center justify-between p-3 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <GitBranch className="h-4 w-4 text-selise-blue" />
                      <span className="text-sm font-medium text-[hsl(var(--fg))]">
                        {t('TEMPLATEBUILDER_PROPERTIESPANEL_CONDITIONALVISIBILITY')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {conditions && conditions.rules.length > 0 ? (
                        <Badge
                          variant="secondary"
                          className="text-xs bg-selise-blue/10 text-selise-blue"
                        >
                          {t('TEMPLATEBUILDER_PROPERTIESPANEL_RULESCOUNT', {
                            count: conditions.rules.length,
                          })}
                        </Badge>
                      ) : (
                        <span className="text-xs text-globe-grey">
                          {t('TEMPLATEBUILDER_PROPERTIESPANEL_NOTSET')}
                        </span>
                      )}
                      <ChevronDown className="h-4 w-4 text-globe-grey" />
                    </div>
                  </button>

                  {/* AI Suggestions Button */}
                  <button
                    type="button"
                    onClick={() => setAiDialogOpen(true)}
                    className="w-full flex items-center justify-between p-3 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-selise-blue" />
                      <span className="text-sm font-medium text-[hsl(var(--fg))]">
                        {t('TEMPLATEBUILDER_PROPERTIESPANEL_AISUGGESTIONS')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {aiEnabled ? (
                        <Badge
                          variant="secondary"
                          className="text-xs bg-lime-green/10 text-poly-green"
                        >
                          {t('TEMPLATEBUILDER_PROPERTIESPANEL_ON')}
                        </Badge>
                      ) : (
                        <span className="text-xs text-globe-grey">
                          {t('TEMPLATEBUILDER_PROPERTIESPANEL_OFF')}
                        </span>
                      )}
                      <ChevronDown className="h-4 w-4 text-globe-grey" />
                    </div>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Condition Editor Dialog */}
      <ConditionEditorDialog
        open={conditionDialogOpen}
        onOpenChange={setConditionDialogOpen}
        targetLabel={field.label}
        targetIndex={fieldIndex}
        conditions={conditions}
        onConditionsChange={handleConditionsChange}
        availableFields={availableFieldsForConditions}
      />

      {/* AI Settings Dialog */}
      <AISettingsDialog
        open={aiDialogOpen}
        onOpenChange={setAiDialogOpen}
        targetLabel={field.label}
        fieldName={field.name}
        targetIndex={fieldIndex}
        aiEnabled={aiEnabled}
        aiKey={aiKey}
        onSave={handleAIChange}
        availableContextKeys={availableContextKeys}
      />
    </motion.div>
  );
}

// Screen properties component
function ScreenProperties({ screen }: { screen: ScreenWithFields }) {
  const { updateScreen, screens } = useBuilder();
  const { t } = useTranslation();
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [conditionDialogOpen, setConditionDialogOpen] = useState(false);
  const [aiEnrichmentDialogOpen, setAiEnrichmentDialogOpen] = useState(false);
  const [conditions, setConditions] = useState<ConditionGroup | null>(() => {
    const screenConditions = (screen as any).conditions;
    if (screenConditions) {
      try {
        return typeof screenConditions === 'string'
          ? JSON.parse(screenConditions)
          : screenConditions;
      } catch {
        return null;
      }
    }
    return null;
  });

  // Local state for text inputs (for immediate UI updates)
  const [localTitle, setLocalTitle] = useState(screen.title);
  const [localDescription, setLocalDescription] = useState(screen.description || '');

  // Debounced update handlers (500ms delay)
  const debouncedUpdateTitle = useDebouncedCallback((value: string) => {
    updateScreen(screen.id, { title: value });
  }, 500);

  const debouncedUpdateDescription = useDebouncedCallback((value: string) => {
    updateScreen(screen.id, { description: value || undefined });
  }, 500);

  // Update conditions and local state when screen changes
  useEffect(() => {
    setLocalTitle(screen.title);
    setLocalDescription(screen.description || '');
    const screenConditions = (screen as any).conditions;
    if (screenConditions) {
      try {
        setConditions(
          typeof screenConditions === 'string' ? JSON.parse(screenConditions) : screenConditions
        );
      } catch {
        setConditions(null);
      }
    } else {
      setConditions(null);
    }
  }, [screen]);

  // Cancel pending updates when screen changes
  useEffect(() => {
    return () => {
      debouncedUpdateTitle.cancel();
      debouncedUpdateDescription.cancel();
    };
  }, [screen.id, debouncedUpdateTitle, debouncedUpdateDescription]);

  // Compute available fields for conditions (fields from all previous screens)
  const availableFieldsForConditions = useMemo<AvailableField[]>(() => {
    const result: AvailableField[] = [];
    const currentScreenIndex = screens.findIndex((s) => s.id === screen.id);

    for (let i = 0; i < currentScreenIndex; i++) {
      const s = screens[i];
      for (const f of s.fields) {
        result.push({
          name: f.name,
          label: f.label,
          screenTitle: s.title,
          type: f.type,
          options: f.options,
        });
      }
    }

    return result;
  }, [screens, screen.id]);

  // Calculate screen index for display
  const screenIndex = useMemo(() => {
    return screens.findIndex((s) => s.id === screen.id) + 1;
  }, [screens, screen.id]);

  const screenType = (screen as any).type || 'standard';
  const selectedTypeOption = screenTypeOptions.find((t) => t.value === screenType);
  const TypeIcon = selectedTypeOption?.icon || FileText;

  const handleTypeChange = (newType: string) => {
    updateScreen(screen.id, { type: newType } as any);
    setShowTypeSelector(false);
  };

  const handleConditionsChange = (newConditions: ConditionGroup | null) => {
    setConditions(newConditions);
    updateScreen(screen.id, {
      conditions: newConditions ? JSON.stringify(newConditions) : null,
    } as any);
  };

  const handleAIEnrichmentSave = (aiPrompt: string | null, aiOutputSchema: string | null) => {
    updateScreen(screen.id, {
      aiPrompt,
      aiOutputSchema,
    } as any);
  };

  const hasLogicConfigured = conditions && conditions.rules.length > 0;

  // Check if AI enrichment is configured
  const hasEnrichmentConfigured = Boolean(
    (screen as any).aiPrompt?.trim() || (screen as any).aiOutputSchema?.trim()
  );

  // Count output keys in schema
  const enrichmentKeyCount = useMemo(() => {
    const schema = (screen as any).aiOutputSchema;
    if (!schema?.trim()) return 0;
    try {
      const parsed = JSON.parse(schema);
      if (parsed.type === 'object' && parsed.properties) {
        return Object.keys(parsed.properties).length;
      }
    } catch {
      return 0;
    }
    return 0;
  }, [screen]);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex-1 overflow-y-auto"
    >
      {/* Header */}
      <div className="p-4 border-b border-border">
        <h3 className="font-semibold text-[hsl(var(--fg))]">
          {t('TEMPLATEBUILDER_PROPERTIESPANEL_SCREENHEADER')}
        </h3>
      </div>

      <div className="p-4 space-y-6">
        {/* Screen Type */}
        <div className="space-y-2">
          <Label className="text-sm text-globe-grey">
            {t('TEMPLATEBUILDER_PROPERTIESPANEL_TYPE')}
          </Label>
          <button
            onClick={() => setShowTypeSelector(!showTypeSelector)}
            className="flex items-center justify-between w-full p-3 rounded-lg border border-border hover:border-selise-blue/50 transition-colors bg-[hsl(var(--bg))]"
          >
            <div className="flex items-center gap-2">
              <TypeIcon className="h-4 w-4 text-globe-grey" />
              <span className="text-sm font-medium text-[hsl(var(--fg))]">
                {t(`TEMPLATEBUILDER_PROPERTIESPANEL_SCREENTYPES_${screenType?.toUpperCase()}`)}
              </span>
            </div>
            <ChevronDown
              className={cn(
                'h-4 w-4 text-globe-grey transition-transform',
                showTypeSelector && 'rotate-180'
              )}
            />
          </button>

          <AnimatePresence>
            {showTypeSelector && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="space-y-1 p-2 mt-2 rounded-lg border border-border bg-muted/30">
                  {screenTypeOptions.map((option) => {
                    const Icon = option.icon;
                    const isSelected = screenType === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => handleTypeChange(option.value)}
                        className={cn(
                          'flex items-center gap-2 w-full p-2 rounded-lg text-left text-sm transition-colors',
                          isSelected
                            ? 'bg-selise-blue text-white'
                            : 'text-[hsl(var(--fg))] hover:bg-muted'
                        )}
                      >
                        <Icon
                          className={cn('h-4 w-4', isSelected ? 'text-white' : 'text-globe-grey')}
                        />
                        <span>
                          {t(
                            `TEMPLATEBUILDER_PROPERTIESPANEL_SCREENTYPES_${option.value?.toUpperCase()}`
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Screen Title */}
        <div className="space-y-2">
          <Label className="text-sm text-[hsl(var(--fg))]">
            {t('TEMPLATEBUILDER_PROPERTIESPANEL_TITLE')}
          </Label>
          <Input
            value={localTitle}
            onChange={(e) => {
              const value = e.target.value;
              setLocalTitle(value);
              debouncedUpdateTitle(value);
            }}
            onBlur={() => {
              debouncedUpdateTitle.flush();
            }}
          />
        </div>

        {/* Screen Description */}
        <div className="space-y-2">
          <Label className="text-sm text-[hsl(var(--fg))]">
            {t('TEMPLATEBUILDER_PROPERTIESPANEL_DESCRIPTION')}
          </Label>
          <Input
            value={localDescription}
            onChange={(e) => {
              const value = e.target.value;
              setLocalDescription(value);
              debouncedUpdateDescription(value);
            }}
            onBlur={() => {
              debouncedUpdateDescription.flush();
            }}
            placeholder={t('TEMPLATEBUILDER_PROPERTIESPANEL_DESCRIPTIONPLACEHOLDER')}
          />
        </div>

        {/* Screen stats */}
        <div className="p-3 rounded-lg bg-muted/30 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-globe-grey">{t('TEMPLATEBUILDER_PROPERTIESPANEL_FIELDS')}</span>
            <span className="font-medium text-[hsl(var(--fg))]">{screen.fields.length}</span>
          </div>
        </div>

        {/* AI Enrichment */}
        <div className="border-t border-border pt-4 space-y-3">
          <button
            type="button"
            onClick={() => setAiEnrichmentDialogOpen(true)}
            className="w-full flex items-center justify-between p-3 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Wand2 className="h-4 w-4 text-selise-blue" />
              <span className="text-sm font-medium text-[hsl(var(--fg))]">
                {t('TEMPLATEBUILDER_PROPERTIESPANEL_AIENRICHMENT')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {hasEnrichmentConfigured ? (
                <Badge variant="secondary" className="text-xs bg-lime-green/10 text-poly-green">
                  {t('TEMPLATEBUILDER_PROPERTIESPANEL_KEYSCOUNT', { count: enrichmentKeyCount })}
                </Badge>
              ) : (
                <span className="text-xs text-globe-grey">
                  {t('TEMPLATEBUILDER_PROPERTIESPANEL_NOTSET')}
                </span>
              )}
              <ChevronDown className="h-4 w-4 text-globe-grey" />
            </div>
          </button>
        </div>

        {/* Conditional Logic */}
        {availableFieldsForConditions.length > 0 && (
          <div className="border-t border-border pt-4 space-y-3">
            <button
              type="button"
              onClick={() => setConditionDialogOpen(true)}
              className="w-full flex items-center justify-between p-3 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <GitBranch className="h-4 w-4 text-selise-blue" />
                <span className="text-sm font-medium text-[hsl(var(--fg))]">
                  {t('TEMPLATEBUILDER_PROPERTIESPANEL_CONDITIONALVISIBILITY')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {hasLogicConfigured ? (
                  <Badge variant="secondary" className="text-xs bg-selise-blue/10 text-selise-blue">
                    {t('TEMPLATEBUILDER_PROPERTIESPANEL_RULESCOUNT', {
                      count: conditions.rules.length,
                    })}
                  </Badge>
                ) : (
                  <span className="text-xs text-globe-grey">
                    {t('TEMPLATEBUILDER_PROPERTIESPANEL_NOTSET')}
                  </span>
                )}
                <ChevronDown className="h-4 w-4 text-globe-grey" />
              </div>
            </button>
          </div>
        )}
      </div>

      {/* Condition Editor Dialog */}
      <ConditionEditorDialog
        open={conditionDialogOpen}
        onOpenChange={setConditionDialogOpen}
        targetLabel={screen.title}
        targetIndex={screenIndex}
        conditions={conditions}
        onConditionsChange={handleConditionsChange}
        availableFields={availableFieldsForConditions}
      />

      {/* AI Enrichment Dialog */}
      <AIEnrichmentDialog
        open={aiEnrichmentDialogOpen}
        onOpenChange={setAiEnrichmentDialogOpen}
        screen={screen as any}
        screenIndex={screenIndex}
        allScreens={screens as any}
        onSave={handleAIEnrichmentSave}
      />
    </motion.div>
  );
}
